import {
  Typography,
  Stack,
  Box,
  Grid,
  Dialog,
  DialogContent,
  Stepper,
  Step,
  StepLabel,
  useMediaQuery,
  Tooltip,
  Autocomplete,
  TextField,
  CircularProgress,
  Select,
  MenuItem,
  SelectChangeEvent,
  IconButton,
  Button,
} from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import Checkbox from '@mui/material/Checkbox';
import { makeStyles } from '@mui/styles';
import CloseModalIcon from '../../../icons/RoundCloseIcon.svg';
import { DocumentTypes, PropertyDTO } from '@inosio/payments-sdk';
import { CustomDropZone } from '../../onboarding/components/CustomDropZone';
import HelpIcon from '@mui/icons-material/Help';
import { selectDocumentState } from '../../../shared/sagas/document/documentSlice';
import { selectPropertiesState } from '../../properties/sagas/pmPropertiesSlice';
import { selectWorkOrderState } from '../../workorders/sagas/workorderSlice';
import { selectVendorsState } from '../../vendors/sagas/vendorsSlice';
import { getUploadOwnerDocuments, selectOwnersState, getResetOwnersState } from '../sagas/ownersSlice';
import { getFriendlyWordsFromDocumentType } from '../utils';
import { ErrorType } from '../../../app/store';
import { getOwnerProperties, selectOwnerDocumentsState } from '../../../ownerui/documents/sagas/documentsSlice';
import { useEffect, useState } from 'react';
import ArrowCircleLeftIcon from '@mui/icons-material/ArrowCircleLeft';
import SpinnerModal from '../../../shared/Modal/SpinnerModal';
interface AddFileModalProps {
  open: boolean;
  ownerUuid: string;
  closeCallback: () => void;
  onSuccessCallback: () => void;
}

const useStyles = makeStyles((theme) => ({
  commonInput: {
    '&.MuiInputBase-root': {
      borderRadius: 24,
    },
    '& .MuiInputBase-root': {
      borderRadius: 24,
    },
  },
  stepperContainer: {
    paddingTop: '50px',
    width: '100%',
  },
  stepper: {
    width: '100%',
    '& .MuiStepLabel-root': {
      display: 'flex',
      flexDirection: 'column-reverse !important',
      marginTop: '-65px',
    },
    '& .MuiStepLabel-label': {
      display: 'flex',
      justifyContent: 'center',
    },
    '& .MuiStepLabel-labelContainer': {
      marginBottom: '9px',
    },
    '& .MuiStepConnector-root': {
      top: '-4px',
    },
    '& .MuiStepIcon-root': {
      color: '#F1F0F5',
      width: '29px',
      height: '29px',
    },
    '& .MuiStepIcon-root.Mui-completed': {
      color: '#39DB8D',
    },
    '& .MuiStepIcon-root .MuiStepIcon-text': {
      fill: '#14142B',
      fontWeight: '600',
    },
    '& .MuiStepIcon-root.Mui-active': {
      color: '#39DB8D',
    },
    '& .MuiStepIcon-root.Mui-active .MuiStepIcon-text': {
      fill: '#fff',
    },
  },
  stepperText: {
    fontFamily: 'Work Sans !important',
    fontStyle: 'normal',
    fontWeight: '400',
    fontSize: '18px !important',
    lineHeight: '21px !important',
    maxWidth: '120px',
    color: '#14142B',
    textWrap: 'nowrap',
  },
}));

const steps = ['Describe File', 'Upload File'];

const defaultStartDate = new Date();
defaultStartDate.setFullYear(defaultStartDate.getFullYear() - 1);
const fileTypeOptions = [
  DocumentTypes.OwnerStatement,
  DocumentTypes.VendorInvoice,
  DocumentTypes.VendorEstimate,
  DocumentTypes.PropertyManagementAgreement,
  DocumentTypes.Lease,
  DocumentTypes.LeaseAddendum,
  DocumentTypes.OwnerDocumentOther,
  DocumentTypes.UtilityBill,
  DocumentTypes.OtherManagementDocuments,
];

export default function AddFileModal(props: AddFileModalProps) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const isOnMobileScreen = useMediaQuery('(max-width:600px)');

  //local states
  const [activeStep, setActiveStep] = useState(0);
  const [fileType, setFileType] = useState<DocumentTypes>(DocumentTypes.VendorInvoice);
  const [properties, setProperties] = useState<PropertyDTO[]>([]);
  const [ownerDocuments, setOwnerDocuments] = useState<Array<any>>([]);
  const [openSpinnerModal, setOpenSpinnerModal] = useState(false);
  const [spinnerStatus, setSpinnerStatus] = useState('');

  //selectors
  const { isUploadingDocumentsFailed, documentErrorMessage } = useSelector(selectDocumentState);
  const { isFetchingProperties, isGetPropertiesFailed, errorMessage: propertiesErrorMessage } = useSelector(selectPropertiesState);
  const workOrderState = useSelector(selectWorkOrderState);
  const { WorkOrdersFetchError, isFetchingWorkOrdersFailed } = workOrderState;
  const { isFetchingVendorsFailed, errorMessage: vendorErrorMessage } = useSelector(selectVendorsState);
  const { isUploadingOwnerDocuments, isUploadingOwnerDocumentsSuccess, isUploadingOwnerDocumentsFailed, errorMessage: uploadOwnerDocumentsErrorMessage } = useSelector(selectOwnersState);
  const { Properties, isFetchingPropertiesSuccess } = useSelector(selectOwnerDocumentsState);

  //flag to keep track if we have selected all
  const areAllPropertiesSelected = properties.length === Properties.length;

  const handleSelectAllProperties = (value: PropertyDTO[]) => {
    // Filter out "Select All" option and only set valid PropertyDTO objects
    const selectedValues = value.filter((v): v is PropertyDTO => (v as PropertyDTO).Uuid !== undefined);

    // Handle "Select All"
    if (value.some((v) => v.Uuid === 'selectAll')) {
      setProperties(areAllPropertiesSelected ? [] : Properties);
    } else {
      setProperties(selectedValues);
    }
  };

  //on load fetch this owner's properties to show them in the dropdown
  useEffect(() => {
    if (props.ownerUuid) {
      dispatch({
        type: getOwnerProperties.type,
        payload: {
          ownerUuid: props.ownerUuid,
        },
      });
    }
  }, [props.ownerUuid]);

  useEffect(() => {
    dispatch(getResetOwnersState());
  }, [props.open]);

  useEffect(() => {
    if (!isUploadingOwnerDocuments && activeStep === 1 && isUploadingOwnerDocumentsSuccess) {
      setSpinnerStatus('stop');
      //The timeout is to stop the modal from closing before the success check mark is shown
      setTimeout(() => {
        props.onSuccessCallback();
        handleCloseModal(); // Close modal after showing the check mark for 2 seconds
      }, 2000); // Wait 2 seconds before closing the modal
    }
  }, [isUploadingOwnerDocuments]);

  useEffect(() => {
    if (isUploadingOwnerDocumentsFailed) {
      setOpenSpinnerModal(false);
    }
  }, [isUploadingOwnerDocumentsFailed]);

  useEffect(() => {
    if (isFetchingPropertiesSuccess && Properties.length === 1 && ![DocumentTypes.OwnerStatement, DocumentTypes.OwnerDocumentOther].includes(fileType)) {
      setProperties(Properties);
    }
  }, [isFetchingPropertiesSuccess, Properties]);

  const handleCloseModal = () => {
    props.closeCallback();
  };

  const handleAddFiles = (acceptedFiles: File[]) => {
    let files = ownerDocuments;

    if (files.length + acceptedFiles.length <= 10) {
      acceptedFiles.forEach((af: File) => {
        if (!files.find((f) => f.name === af.name)) {
          files = [...files, af];
        }
      });
      setOwnerDocuments(files);
    }
  };
  const handleRemoveFiles = (file: any) => {
    const files = ownerDocuments.filter((f: File) => f.name !== file.name);
    setOwnerDocuments(files);
  };

  const handleUploadDocuments = async () => {
    const uploadingFiles = ownerDocuments?.filter((f) => f.name && !f.remoteUuid);
    if (uploadingFiles.length > 0) {
      setOpenSpinnerModal(true);
      dispatch({
        type: getUploadOwnerDocuments.type,
        payload: {
          belongsToEntityUuid: props.ownerUuid,
          documents: uploadingFiles,
          documentType: fileType,
          propertyUuids: properties.map((p) => p.Uuid),
        },
      });
    }
  };

  let globalError = {
    message: '',
    statusCode: 0,
  } as ErrorType;
  if (isUploadingDocumentsFailed) {
    globalError = documentErrorMessage;
  } else if (isGetPropertiesFailed) {
    globalError = propertiesErrorMessage;
  } else if (isFetchingWorkOrdersFailed) {
    globalError = WorkOrdersFetchError;
  } else if (isFetchingVendorsFailed) {
    globalError = vendorErrorMessage;
  } else if (uploadOwnerDocumentsErrorMessage) {
    globalError = uploadOwnerDocumentsErrorMessage;
  }

  return (
    <Dialog
      open={props.open}
      onClose={handleCloseModal}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      fullScreen={isOnMobileScreen}
      fullWidth={true}
      disableEnforceFocus
      style={{
        background: 'none',
      }}
      PaperProps={{
        style: {
          pointerEvents: 'auto',
          width: '472px',
          height: '523px',
          borderRadius: '24px',
        },
      }}
    >
      <DialogContent sx={{ padding: 0, display: 'flex' }}>
        <Box component="form" noValidate display={'flex'} flexDirection={'column'} width="100%" height="100%" padding="30px 24px 0">
          <Grid container spacing={2}>
            <Grid item xs={1}>
              {activeStep === 1 && (
                <IconButton
                  aria-label="go to prev"
                  disabled={activeStep === 1 && isUploadingOwnerDocuments.valueOf()}
                  onClick={() => {
                    setActiveStep(activeStep - 1);
                  }}
                  sx={{
                    position: 'absolute',
                    left: 0,
                  }}
                >
                  <ArrowCircleLeftIcon sx={{ width: '35px', height: '35px', fill: '#39A2DB' }} />
                </IconButton>
              )}
            </Grid>
            <Grid item xs={10}>
              <Box className={classes.stepperContainer} sx={{ display: isOnMobileScreen ? 'none' : 'block' }}>
                <Stepper alternativeLabel activeStep={activeStep} classes={{ root: classes.stepper }}>
                  {steps.map((label) => (
                    <Step key={label}>
                      <StepLabel sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Typography className={classes.stepperText}>{label}</Typography>
                      </StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Box>
            </Grid>
            <Grid item xs={1}>
              <IconButton
                onClick={() => {
                  handleCloseModal();
                }}
              >
                <img src={CloseModalIcon} alt="close" style={{ cursor: 'pointer' }} />
              </IconButton>
            </Grid>
            <Grid item xs={12}>
              {globalError?.message?.length ? (
                <Typography
                  style={{
                    fontFamily: "'Open Sans', sans-serif",
                    fontStyle: 'normal',
                    fontWeight: 500,
                    fontSize: '14px',
                    lineHeight: '18px',
                    color: '#DB3939',
                    textAlign: 'center',
                    borderTop: '2px  solid #DB3939',
                    borderRadius: '24px',
                    padding: '12px 20px',
                    width: '100%',
                    margin: '0px auto',
                  }}
                >
                  {globalError.message}
                </Typography>
              ) : (
                ''
              )}
            </Grid>
          </Grid>
          <Stack direction="column" spacing={3} marginTop={3} marginBottom={'auto'}>
            {activeStep === 0 && (
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                  <Typography className={classes.stepperText}>File Type:</Typography>
                  <Select
                    className={classes.commonInput}
                    sx={{ width: '100%', marginLeft: 8 }}
                    size="small"
                    labelId="file-type-select"
                    id="file-type-select"
                    value={fileType as DocumentTypes}
                    label=""
                    onChange={(event: SelectChangeEvent) => {
                      setFileType(event.target.value as DocumentTypes);
                    }}
                  >
                    {fileTypeOptions.map((option: DocumentTypes) => (
                      <MenuItem value={option} key={option}>
                        {option === DocumentTypes.OwnerDocumentOther ? 'Other' : getFriendlyWordsFromDocumentType(option)}
                      </MenuItem>
                    ))}
                  </Select>
                </Stack>
                <Stack direction="column" spacing={2} marginTop={4}>
                  <Typography className={classes.stepperText}>Select Properties:</Typography>
                  <Autocomplete
                    multiple
                    id="properties-address"
                    className={classes.commonInput}
                    size="small"
                    options={[{ Uuid: 'selectAll', Name: 'Select All' } as PropertyDTO, ...Properties]} // Add "Select All" option
                    getOptionLabel={(option: PropertyDTO) => option.Name}
                    disableCloseOnSelect
                    loading={isFetchingProperties.valueOf()}
                    value={properties}
                    onChange={(event, value) => handleSelectAllProperties(value)}
                    filterOptions={(options, { inputValue }) => {
                      // Filter options based on input value
                      const filtered = options.filter((option) => option.Name.toLowerCase().includes(inputValue.toLowerCase()));

                      // Add the "Select All" option to the start of the list if it's filtered
                      const selectAllOption = options.find((option) => option.Uuid === 'selectAll');

                      // Ensure "Select All" is always at the top if present in the filtered results
                      if (selectAllOption) {
                        return [selectAllOption, ...filtered.filter((option) => option !== selectAllOption)];
                      }

                      return filtered; // Return the filtered options if "Select All" is not present
                    }}
                    renderOption={(props, option, { selected }) => {
                      // Special rendering for "Select All"
                      if (option.Uuid === 'selectAll') {
                        return (
                          <li {...props}>
                            <Checkbox indeterminate={properties.length > 0 && !areAllPropertiesSelected} checked={areAllPropertiesSelected} style={{ marginRight: 8 }} />
                            {option.Name}
                          </li>
                        );
                      }
                      // Default rendering for actual properties
                      return (
                        <li {...props}>
                          <Checkbox style={{ marginRight: 8 }} checked={selected} />
                          {option.Name}
                        </li>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label="Property Name"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {isFetchingProperties ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Stack>
              </Box>
            )}
            {activeStep === 1 && (
              <Stack>
                <Typography
                  style={{
                    fontWeight: 700,
                    fontSize: '18px',
                    lineHeight: '25px',
                    paddingLeft: '0px',
                    paddingTop: '0px',
                    color: '#14142B',
                  }}
                  sx={{ p: 2 }}
                >
                  {'Add File(s)'}
                  <Tooltip title="Add one or multiple files that you want to include in this upload." placement="right">
                    <HelpIcon sx={{ fontSize: 17, color: '#C4C4C4', cursor: 'pointer', ml: '5px' }} />
                  </Tooltip>
                </Typography>

                <CustomDropZone
                  maxFileSize={20}
                  files={ownerDocuments}
                  fileLimit={10}
                  handleFilesAdded={handleAddFiles}
                  handleFileDeleted={handleRemoveFiles}
                  description="Max 20MB per file. Up to 10 files at a time."
                  height="250px"
                  fileTypes={[
                    'image/png',
                    'image/jpeg',
                    'application/pdf',
                    'text/plain',
                    'text/csv',
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                    'application/vnd.ms-excel',
                    'application/zip',
                    'application/rtf',
                    'application/msword',
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                  ]} //.txt
                />
                <Typography style={{ fontFamily: 'Work Sans', fontSize: '14px' }}>{'Supported File Types: .xlsx, .xls, .csv, .jpg, .png, .doc, .docx, .rtf, .txt, .jpeg, .pdf, .zip'}</Typography>
              </Stack>
            )}
          </Stack>

          <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2} marginTop={'auto'} paddingBottom={2.5}>
            <Button
              variant="contained"
              id="add-bank-btn"
              className={true ? '' : 'active'} //it's for e2e test
              disabled={!fileType || (!properties.length && ![DocumentTypes.OwnerStatement, DocumentTypes.OwnerDocumentOther].includes(fileType))}
              onClick={() => {
                if (activeStep === 0) {
                  if (!!fileType) {
                    setActiveStep(1);
                  }
                } else if (activeStep === 1) {
                  handleUploadDocuments();
                }
              }}
            >
              {activeStep === 0 ? 'Continue' : 'Upload'}
            </Button>
          </Stack>

          <SpinnerModal
            openSpinnerModal={openSpinnerModal}
            handleCloseSpinnerModal={() => {
              setOpenSpinnerModal(false);
            }}
            spinnerStatus={spinnerStatus}
            closeCancel={() => {}}
            width="472px"
            height="523px"
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
}
